import type { ApiResponse, Pagination } from "@/types";

import { BaseApi } from "../base.api";
import type {
  AtRiskQuery,
  CohortQuery,
  CohortRow,
  CourseFunnel,
  PayoutMonth,
  PayoutRecord,
  AtRiskRow,
  CourseReportQuery,
  CourseReportRow,
  ModerationAction,
  ModerationActionsQuery,
  ModeratorReport,
  ReportWindow,
  StudentAnalytics,
  StudentReportQuery,
  StudentReportRow,
  TeacherAnalytics,
  TeacherReportQuery,
  TeacherReportRow,
  WeeklyRunResult,
} from "./types";

export * from "./types";

/** Eksport sahifasiz: `page`/`limit` unga tegishli emas. */
type Unpaged<T> = Omit<T, "page" | "limit">;

/**
 * Hisobotlar API — backend'dagi `ReportsController` ning ko'zgusi.
 *
 * `*Export` usullari ro'yxat bilan BIR XIL filtr va saralashni oladi, lekin
 * sahifasiz (ko'pi bilan 5000 qator) — Excel fayli shu javobdan brauzerda
 * yig'iladi. Ekrandagi jadval va yuklab olingan fayl shu tufayli hech
 * qachon bir-biridan farq qilmaydi.
 */
export class ReportsApi extends BaseApi {
  private readonly baseUrl: string = "/reports";

  teachers(query: TeacherReportQuery) {
    return this.request.get<TeacherReportRow[], Pagination<TeacherReportRow>>(
      `${this.baseUrl}/teachers`,
      { params: query },
    );
  }

  teachersExport(query: Unpaged<TeacherReportQuery>) {
    return this.request.get<TeacherReportRow[], ApiResponse<TeacherReportRow[]>>(
      `${this.baseUrl}/teachers/export`,
      { params: query },
    );
  }

  teacher(id: number, window: ReportWindow = {}) {
    return this.request.get<TeacherAnalytics, ApiResponse<TeacherAnalytics>>(
      `${this.baseUrl}/teachers/${id}`,
      { params: window },
    );
  }

  students(query: StudentReportQuery) {
    return this.request.get<StudentReportRow[], Pagination<StudentReportRow>>(
      `${this.baseUrl}/students`,
      { params: query },
    );
  }

  studentsExport(query: Unpaged<StudentReportQuery>) {
    return this.request.get<StudentReportRow[], ApiResponse<StudentReportRow[]>>(
      `${this.baseUrl}/students/export`,
      { params: query },
    );
  }

  student(id: number, window: ReportWindow = {}) {
    return this.request.get<StudentAnalytics, ApiResponse<StudentAnalytics>>(
      `${this.baseUrl}/students/${id}`,
      { params: window },
    );
  }

  courses(query: CourseReportQuery) {
    return this.request.get<CourseReportRow[], Pagination<CourseReportRow>>(
      `${this.baseUrl}/courses`,
      { params: query },
    );
  }

  coursesExport(query: Unpaged<CourseReportQuery>) {
    return this.request.get<CourseReportRow[], ApiResponse<CourseReportRow[]>>(
      `${this.baseUrl}/courses/export`,
      { params: query },
    );
  }

  atRisk(query: AtRiskQuery) {
    return this.request.get<AtRiskRow[], Pagination<AtRiskRow>>(`${this.baseUrl}/at-risk`, {
      params: query,
    });
  }

  atRiskExport(query: Unpaged<AtRiskQuery>) {
    return this.request.get<AtRiskRow[], ApiResponse<AtRiskRow[]>>(
      `${this.baseUrl}/at-risk/export`,
      { params: query },
    );
  }

  moderators(window: ReportWindow = {}) {
    return this.request.get<ModeratorReport, ApiResponse<ModeratorReport>>(
      `${this.baseUrl}/moderators`,
      { params: window },
    );
  }

  /** Moderatorning o'z hisoboti; navbat holati esa platforma bo'yicha. */
  myModeration(window: ReportWindow = {}) {
    return this.request.get<ModeratorReport, ApiResponse<ModeratorReport>>(
      `${this.baseUrl}/moderators/me`,
      { params: window },
    );
  }

  moderatorActions(moderatorId: number, query: ModerationActionsQuery) {
    return this.request.get<ModerationAction[], Pagination<ModerationAction>>(
      `${this.baseUrl}/moderators/${moderatorId}/actions`,
      { params: query },
    );
  }

  myModerationActions(query: ModerationActionsQuery) {
    return this.request.get<ModerationAction[], Pagination<ModerationAction>>(
      `${this.baseUrl}/moderators/me/actions`,
      { params: query },
    );
  }

  /** Kogortalar: yozilgan oyi bo'yicha tugatish va faol qolish. */
  cohorts(query: CohortQuery = {}) {
    return this.request.get<CohortRow[], ApiResponse<CohortRow[]>>(`${this.baseUrl}/cohorts`, {
      params: query,
    });
  }

  /** Kurs voronkasi: darslar bo'ylab boshlagan va tugatganlar. */
  courseFunnel(courseId: number) {
    return this.request.get<CourseFunnel, ApiResponse<CourseFunnel>>(
      `${this.baseUrl}/courses/${courseId}/funnel`,
    );
  }

  /** Oy bo'yicha o'qituvchilarga to'lovlar (standart — o'tgan oy). */
  payouts(period?: string) {
    return this.request.get<PayoutMonth, ApiResponse<PayoutMonth>>(`${this.baseUrl}/payouts`, {
      params: { period },
    });
  }

  recordPayout(data: { teacherId: number; period: string; reference?: string }) {
    return this.request.post<PayoutRecord, ApiResponse<PayoutRecord>>(
      `${this.baseUrl}/payouts`,
      data,
    );
  }

  cancelPayout(id: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/payouts/${id}`);
  }

  /** O'tgan hafta hisobotini qo'lda qayta yuborish (odatda dushanba 09:00 da o'zi ketadi). */
  runWeekly() {
    return this.request.post<WeeklyRunResult, ApiResponse<WeeklyRunResult>>(
      `${this.baseUrl}/weekly/run`,
    );
  }
}

export const reportsApi = new ReportsApi();
